import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, map, of, take } from 'rxjs';

import { DynamicTicketType } from '@app/services/dynamic-ticket-type.service';
import { PuSubscribable } from '@app/utils/pu-subscribe';
import { CustomRemindDialogComponent } from '@main-application/inspections/components/inspection-add-dialog/custom-remind-dialog/custom-remind-dialog.component';
import {
  CustomNotifyId,
  DontNotifyId,
  DontNotifyOption,
  OnDueDateNotifyId,
} from '@main-application/inspections/components/inspection-add-dialog/inspection-add-regular-form/inspection-add-regular-form.component';
import { GetInspectionRemindListConst } from '@main-application/inspections/models/inspection-notify-list.const';
import { selectTenantData } from '@main-application/store/selectors/user.selectors';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { isExist } from '@shared/utils/exist.util';
import { InputModule } from '@ui-components/controls/input/input.module';
import { RadioButtonDropdownModule } from '@ui-components/controls/radio-button-dropdown/radio-button-dropdown.module';

export const CompletedId = 30000;
const UntilCompletedOption: IRadioButtonOption<number> = {
  value: CompletedId,
  label: `Completed`,
  sort: -CompletedId,
};

export type CustomNotificationFieldType = 'notifyOn' | 'remindOn' | 'until';

export enum BuiltInTicketType {
  MakeReady = 1,
  ResidentTicket = 2,
  ReplacementTicket = 3,
  Custom = 4,
  MakeReadyCost = 5,
  WorkOrder = 6,
}

export enum DynamicTicketTypeNotifyAssigneeMode {
  DontNotify,
  OnTicketCreation,
  OnDueDate,
  Custom,
}

export const BuiltInTicketTypeOptions: IRadioButtonOption<number>[] = [
  { value: BuiltInTicketType.Custom, label: 'Custom' },
  { value: BuiltInTicketType.MakeReady, label: 'Make ready' },
  { value: BuiltInTicketType.MakeReadyCost, label: 'Make ready cost' },
  { value: BuiltInTicketType.ResidentTicket, label: 'Resident ticket' },
  { value: BuiltInTicketType.ReplacementTicket, label: 'Replacement ticket' },
  { value: BuiltInTicketType.WorkOrder, label: 'Work Order' },
];

export const NotifyAssigneeOptions: IRadioButtonOption<number>[] = [
  { value: DynamicTicketTypeNotifyAssigneeMode.Custom, label: 'Custom notifications' },
  { value: DynamicTicketTypeNotifyAssigneeMode.DontNotify, label: `Don't notify` },
  { value: DynamicTicketTypeNotifyAssigneeMode.OnDueDate, label: 'On due date' },
  { value: DynamicTicketTypeNotifyAssigneeMode.OnTicketCreation, label: 'On ticket creation' },
];

export interface AddTicketTypeDialogData extends Partial<DynamicTicketType> {
  isWorkOrder?: boolean;
}

@Component({
  standalone: true,
  selector: 'app-add-ticket-type-dialog',
  templateUrl: './add-ticket-type-dialog.component.html',
  styleUrls: ['./add-ticket-type-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    InputModule,
    RadioButtonDropdownModule,
  ],
})
export class AddTicketTypeDialogComponent extends PuSubscribable implements OnInit {
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private dialogRef: MatDialogRef<AddTicketTypeDialogComponent> = inject(MatDialogRef);
  private dialog: MatDialog = inject(MatDialog);
  private store: Store<{}> = inject(Store);

  protected readonly DynamicTicketTypeNotifyAssigneeMode = DynamicTicketTypeNotifyAssigneeMode;
  public data: AddTicketTypeDialogData = inject(MAT_DIALOG_DATA, { optional: true }) ?? {};
  readonly BuiltInTicketType = BuiltInTicketType;
  readonly isEditMode = !!this.data?.id;

  form = this.formBuilder.group({
    name: [null],
    builtInType: [null],
    notifyAssignee: [DynamicTicketTypeNotifyAssigneeMode.OnDueDate],
    notifyOn: [OnDueDateNotifyId],
    remindOn: [DontNotifyId],
    until: [CompletedId],
  });

  notifyOptions = this.createNotifyOptions('notifyOn', GetInspectionRemindListConst('remindOn'));
  remindOnOptions = this.createNotifyOptions('remindOn', GetInspectionRemindListConst('thenRemindOn'), true);
  untilOptions = this.createNotifyOptions('until', GetInspectionRemindListConst('until'), true);

  DontNotifyId = DontNotifyId;
  builtInTicketTypeOptions = BuiltInTicketTypeOptions;
  notifyAssigneeOptions = NotifyAssigneeOptions;

  submitted = false;

  get remindOn(): UntypedFormControl {
    return this.form.get('remindOn') as UntypedFormControl;
  }

  pmsNames$ = this.store
    .select(selectTenantData)
    .pipe(map(tenantData => (tenantData?.pmses && tenantData.pmses !== '' ? tenantData.pmses : 'PMS')));

  ticketTypeDescription$ = combineLatest([this.pmsNames$, of(this.data?.isWorkOrder ?? false)]).pipe(
    map(([pmsNames, isWorkOrder]) => {
      return isWorkOrder
        ? `Work Order synced with ${pmsNames}`
        : 'The ticket is an internal entity and is used for PropUp processes only';
    })
  );

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.initForm();
    this.initFormChanges();
  }

  close() {
    this.dialogRef.close();
  }

  get dialogTitle(): string {
    if (!this.isEditMode) {
      return this.data?.isWorkOrder ? 'Add Work Order Type' : 'Add PropUp Ticket Type';
    }
    const ticketKind = this.data.builtInType === BuiltInTicketType.WorkOrder ? 'Work Order' : 'PropUp';
    return `Edit ${ticketKind} ${this.data.name}`;
  }

  save() {
    this.submitted = true;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.cdr.detectChanges();
      return;
    }
    this.dialogRef.close(this.form.value);
  }

  private openCustomDateDialog(fieldType: CustomNotificationFieldType = 'notifyOn', prevValue?: number) {
    this.dialog
      .open(CustomRemindDialogComponent, {
        panelClass: 'modal-container-without-padding',
        width: '424px',
        autoFocus: false,
        data: {
          fieldType: fieldType === 'remindOn' ? 'thenRemindOn' : fieldType,
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe(res => {
        if (res === undefined) {
          this.form.get(fieldType).setValue(prevValue ?? DontNotifyId);
          return;
        }

        const days = res.days;
        const options = this.getOptions(fieldType);
        const option = options.find(o => o.value === days);
        if (!option) {
          const newOption = this.createNewOption(fieldType, days);
          this.form.get(fieldType).setValue(newOption.value);
        } else {
          this.form.get(fieldType).setValue(option.value);
        }
      })
      .untilDestroyed(this);
  }

  private createNewOption(fieldType: CustomNotificationFieldType, value: number): IRadioButtonOption<number> {
    const label = this.getOptionLabel(fieldType, value);
    const newOption = { value: value, label, sort: value };
    const options = this.getOptions(fieldType);
    options.push(newOption);
    this.updateOptions(fieldType, options);
    return newOption;
  }

  private getOptionLabel(fieldType: CustomNotificationFieldType, value: number): string {
    if (fieldType === 'until') {
      return value === 1 ? '1 time' : `${value} times`;
    }
    if (fieldType === 'remindOn') {
      return `Every ${value}d`;
    }
    return value < 0 ? `${Math.abs(value)}d before due date` : `${value}d after due date`;
  }

  private getOptions(fieldType: CustomNotificationFieldType): IRadioButtonOption<number>[] {
    switch (fieldType) {
      case 'notifyOn':
        return [...this.notifyOptions[1]];
      case 'remindOn':
        return [...this.remindOnOptions[1]];
      case 'until':
        return [...this.untilOptions[1]];
    }
    return [];
  }

  updateOptions(fieldType: CustomNotificationFieldType, newOptions: IRadioButtonOption<number>[]) {
    switch (fieldType) {
      case 'notifyOn':
        this.notifyOptions = this.createNotifyOptions(
          'notifyOn',
          newOptions.sort((a, b) => +a.sort - +b.sort)
        );
        break;

      case 'remindOn':
        this.remindOnOptions = this.createNotifyOptions(
          'remindOn',
          newOptions.sort((a, b) => +a.sort - +b.sort)
        );
        break;
      case 'until':
        this.untilOptions = this.createNotifyOptions(
          'until',
          newOptions.sort((a, b) => +a.sort - +b.sort)
        );
        break;
    }
  }

  private createNotifyOptions(
    fieldType: CustomNotificationFieldType,
    notifyOptions: IRadioButtonOption<number>[],
    addEmptyOption = false
  ): IRadioButtonOption<number>[][] {
    switch (fieldType) {
      case 'notifyOn':
        const customNotifyOn = this.getCustomOption();
        if (addEmptyOption) {
          return [[customNotifyOn], [DontNotifyOption, ...notifyOptions]];
        }
        return [[customNotifyOn], notifyOptions];

      case 'remindOn':
        const customThenRemind = this.getCustomOption('Set custom frequency');
        if (addEmptyOption) {
          return [[customThenRemind], [{ ...DontNotifyOption, label: "Don't remind" }, ...notifyOptions]];
        }
        return [[customThenRemind], notifyOptions];

      case 'until':
        const customUntil = this.getCustomOption('Set custom');
        if (addEmptyOption) {
          return [[customUntil], [UntilCompletedOption, ...notifyOptions]];
        }
        return [[customUntil], notifyOptions];
    }
  }

  getCustomOption(label = 'Set custom timing') {
    return {
      value: CustomNotifyId,
      label,
      sort: 0,
    };
  }

  private initForm() {
    const notifyOn = this.data?.notifyAssigneeOnCustom;
    const remindOn = this.data?.remindAssigneeEveryNDays;
    const until = this.data?.remindAssigneeStopAfterNReminders;
    if (isExist(notifyOn) && !this.notifyOptions[1].find(o => o.value === notifyOn)) {
      this.createNewOption('notifyOn', notifyOn);
    }

    if (remindOn > 0 && !this.remindOnOptions[1].find(o => o.value === remindOn)) {
      this.createNewOption('remindOn', remindOn);
    }
    if (until > 0 && !this.untilOptions[1].find(o => o.value === until)) {
      this.createNewOption('until', until);
    }
    // Set builtInType automatically: WorkOrder = 6, PropUp ticket = 4 (Custom)
    const defaultBuiltInType =
      this.data?.builtInType ?? (this.data?.isWorkOrder ? BuiltInTicketType.WorkOrder : BuiltInTicketType.Custom);
    // Set notifyAssignee to default value (OnDueDate) if not provided
    const defaultNotifyAssignee = this.data?.notifyAssignee ?? DynamicTicketTypeNotifyAssigneeMode.OnDueDate;
    this.form = this.formBuilder.group({
      name: [this.data?.name || null, [Validators.required]],
      builtInType: [defaultBuiltInType, [Validators.required]],
      notifyAssignee: [defaultNotifyAssignee],
      notifyOn: [notifyOn ?? OnDueDateNotifyId],
      remindOn: [remindOn ?? DontNotifyId],
      until: [until ?? CompletedId],
    });
    this.cdr.detectChanges();
  }

  private initFormChanges() {
    let prevNotifyOnValue = this.form.controls.notifyOn.value;
    this.form.controls.notifyOn.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(notifyOn => {
        if (notifyOn === CustomNotifyId) {
          this.openCustomDateDialog('notifyOn', prevNotifyOnValue);
        }
        prevNotifyOnValue = notifyOn;

        if (notifyOn === DontNotifyId) {
          this.form.controls.remindOn.setValue(DontNotifyId);
          this.form.controls.until.setValue(CompletedId);
        }
      })
      .untilDestroyed(this);

    let prevRemindOnValue = this.form.controls.remindOn.value;
    this.form.controls.remindOn.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(remindOn => {
        if (remindOn === CustomNotifyId) {
          this.openCustomDateDialog('remindOn', prevRemindOnValue);
        }
        prevRemindOnValue = remindOn;

        if (remindOn === DontNotifyId && this.form.value.until !== CompletedId) {
          this.form.controls.until.setValue(CompletedId);
        }
      })
      .untilDestroyed(this);

    let prevUntilValue = this.form.controls.until.value;
    this.form.controls.until.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(until => {
        if (until === CustomNotifyId) {
          this.openCustomDateDialog('until', prevUntilValue);
        }
        prevUntilValue = until;
      })
      .untilDestroyed(this);
  }
}
